import { RefObject, useEffect } from 'react';

// Pinch-zoom over the camera video, extracted from the former Archive page.
// It applies the track's optical zoom (`applyConstraints({ zoom })`) with
// the two-finger gesture; if the camera does not expose `zoom` in its
// capabilities, it does nothing. It only makes sense on touch.

type ZoomCapabilities = MediaTrackCapabilities & {
  zoom?: { min?: number; max?: number; step?: number };
};

const distance = (t1: Touch, t2: Touch): number =>
  Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);

export const usePinchZoom = (
  videoRef: RefObject<HTMLVideoElement>,
  enabled: boolean,
) => {
  useEffect(() => {
    const video = videoRef.current;
    if (!enabled || !video) return;

    let zoom = 0;
    let pinchStartDist: number | null = null;
    let pinchStartZoom = 0;

    const getTrack = () => {
      const stream = video.srcObject as MediaStream | null;
      return stream?.getVideoTracks?.()[0] ?? null;
    };

    const applyZoom = async (requested: number) => {
      const track = getTrack();
      if (!track || !track.getCapabilities) return;

      const caps = track.getCapabilities() as ZoomCapabilities;
      if (!caps.zoom) return;

      const min = caps.zoom.min ?? 1;
      const max = caps.zoom.max ?? 1;
      const step = caps.zoom.step ?? 0.1;

      let next = Math.max(min, Math.min(max, requested));
      next = Math.round(next / step) * step;

      await track.applyConstraints({
        advanced: [{ zoom: next } as Record<string, number>],
      });
      zoom = next;
    };

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        pinchStartDist = distance(e.touches[0], e.touches[1]);
        pinchStartZoom = zoom || 1;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 2 || !pinchStartDist) return;
      // Prevents the page's own zoom.
      e.preventDefault();

      const scale = distance(e.touches[0], e.touches[1]) / pinchStartDist;
      applyZoom(pinchStartZoom * scale).catch(() => {});
    };

    const onTouchEnd = () => {
      pinchStartDist = null;
    };

    video.addEventListener('touchstart', onTouchStart, { passive: true });
    video.addEventListener('touchmove', onTouchMove, { passive: false });
    video.addEventListener('touchend', onTouchEnd);

    return () => {
      video.removeEventListener('touchstart', onTouchStart);
      video.removeEventListener('touchmove', onTouchMove);
      video.removeEventListener('touchend', onTouchEnd);
    };
  }, [videoRef, enabled]);
};
